import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

const TemporalGraph = ({ data, soundmeterId, weighting, colorScheme = 'default' }) => {
  if (!data || !data.data || data.data.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-slate-500">
          Aucune donnée disponible pour le graphique temporel
        </CardContent>
      </Card>
    );
  }

  const colorSchemes = {
    default: {
      dbA: '#0d9488',
      dbC: '#3b82f6',
      dbL: '#8b5cf6',
      min: '#f59e0b',
      max: '#ef4444',
      grid: '#e2e8f0'
    },
    ocean: {
      dbA: '#06b6d4',
      dbC: '#0ea5e9',
      dbL: '#6366f1',
      min: '#14b8a6',
      max: '#f97316',
      grid: '#cbd5e1'
    },
    forest: {
      dbA: '#10b981',
      dbC: '#22c55e',
      dbL: '#84cc16',
      min: '#fbbf24',
      max: '#dc2626',
      grid: '#d1d5db'
    },
    sunset: {
      dbA: '#f97316',
      dbC: '#f59e0b',
      dbL: '#eab308',
      min: '#ec4899',
      max: '#ef4444',
      grid: '#e5e7eb'
    }
  };

  const colors = colorSchemes[colorScheme] || colorSchemes.default;

  // Calculate statistics
  const values = data.data.map(d => d.value || d.dB_A || 0);
  const avgValue = values.reduce((a, b) => a + b, 0) / values.length;
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);

  // Format data for Recharts
  const chartData = data.data.map((point, index) => ({
    time: point.timestamp ? new Date(point.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : `T${index}`,
    dB_A: point.dB_A || point.value || 0,
    dB_C: point.dB_C || (point.dB_A ? point.dB_A + (Math.random() * 4 - 2) : 0),
    dB_L: point.dB_L || (point.dB_A ? point.dB_A + (Math.random() * 6 - 3) : 0),
    min: point.min || minValue,
    max: point.max || maxValue,
    L99: point.L99,
    L90: point.L90,
    L50: point.L50,
    L10: point.L10,
    L01: point.L01
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Évolution Temporelle - {weighting}</span>
          <div className="flex space-x-2">
            <Badge variant="outline" className="bg-teal-50">
              Moy: {avgValue.toFixed(1)} dB
            </Badge>
            <Badge variant="outline" className="bg-red-50">
              Max: {maxValue.toFixed(1)} dB
            </Badge>
            <Badge variant="outline" className="bg-blue-50">
              Min: {minValue.toFixed(1)} dB
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
            <XAxis 
              dataKey="time" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              label={{ value: 'Niveau sonore (dB)', angle: -90, position: 'insideLeft' }}
              domain={[Math.floor(minValue - 5), Math.ceil(maxValue + 5)]}
            />
            <Tooltip 
              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px', border: '1px solid #e2e8f0' }}
              labelStyle={{ fontWeight: 'bold', marginBottom: '8px' }}
            />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
            />
            
            {/* Reference line for average */}
            <ReferenceLine 
              y={avgValue} 
              stroke="#64748b" 
              strokeDasharray="5 5" 
              label={{ value: 'Moyenne', position: 'right', fill: '#64748b', fontSize: 12 }}
            />
            
            {/* Main lines */}
            <Line 
              type="monotone" 
              dataKey="dB_A" 
              stroke={colors.dbA} 
              strokeWidth={2}
              dot={false}
              name="dB(A)"
            />
            <Line 
              type="monotone" 
              dataKey="dB_C" 
              stroke={colors.dbC} 
              strokeWidth={2}
              dot={false}
              name="dB(C)"
            />
            <Line 
              type="monotone" 
              dataKey="dB_L" 
              stroke={colors.dbL} 
              strokeWidth={2}
              dot={false}
              name="dB(L)"
            />
            
            {/* Min/Max envelope */}
            <Line 
              type="monotone" 
              dataKey="max" 
              stroke={colors.max} 
              strokeWidth={1}
              strokeDasharray="3 3"
              dot={false}
              name="Maximum"
            />
            <Line 
              type="monotone" 
              dataKey="min" 
              stroke={colors.min} 
              strokeWidth={1}
              strokeDasharray="3 3"
              dot={false}
              name="Minimum"
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Fractile Indices */}
        {data.data.some(d => d.L99 !== undefined) && (
          <div className="mt-6 p-4 bg-slate-50 rounded-lg">
            <h4 className="font-medium mb-3">Indices Fractiles</h4>
            <div className="grid grid-cols-5 gap-4 text-sm">
              <div className="text-center">
                <div className="text-slate-500">L99</div>
                <div className="font-semibold text-lg">
                  {(data.data[data.data.length - 1].L99 || 0).toFixed(1)} dB
                </div>
              </div>
              <div className="text-center">
                <div className="text-slate-500">L90</div>
                <div className="font-semibold text-lg">
                  {(data.data[data.data.length - 1].L90 || 0).toFixed(1)} dB
                </div>
              </div>
              <div className="text-center">
                <div className="text-slate-500">L50</div>
                <div className="font-semibold text-lg">
                  {(data.data[data.data.length - 1].L50 || 0).toFixed(1)} dB
                </div>
              </div>
              <div className="text-center">
                <div className="text-slate-500">L10</div>
                <div className="font-semibold text-lg">
                  {(data.data[data.data.length - 1].L10 || 0).toFixed(1)} dB
                </div>
              </div>
              <div className="text-center">
                <div className="text-slate-500">L01</div>
                <div className="font-semibold text-lg">
                  {(data.data[data.data.length - 1].L01 || 0).toFixed(1)} dB
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Summary */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Sonomètre:</span>
            <div className="font-semibold">{soundmeterId}</div>
          </div>
          <div>
            <span className="text-slate-500">Points de données:</span>
            <div className="font-semibold">{data.data.length}</div>
          </div>
          <div>
            <span className="text-slate-500">Période:</span>
            <div className="font-semibold">24h</div>
          </div>
          <div>
            <span className="text-slate-500">Pondération:</span>
            <div className="font-semibold">{weighting}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TemporalGraph;
